import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Sparkles, Layout, GraduationCap, Send,
  Loader2, // Added Loader2 back for the button icon
  Users, Upload, X, Paperclip, FileText, Youtube,
  Image as ImageIcon, Video, Trash2, Edit3, FileCheck, Link as LinkIcon
} from 'lucide-react';
import './CreateCourse.scss';
import HeaderBar from '../../../common/HeaderBar';
import SideBar from '../../../common/SideBar';

import { handleAsyncAction } from '../../../../utils/loader/helper';

const CourseManagement = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [activityData, setActivityData] = useState({});
  const [selectedType, setSelectedType] = useState("");

  const typeIcons = {
    page: <ImageIcon size={14} className="icon-page" />,
    pdf: <FileText size={14} className="icon-pdf" />,
    youtube: <Youtube size={14} className="icon-youtube" />,
    video: <Video size={14} className="icon-video" />
  };

  const handleAddActivity = (type) => {
    if (!type) return;
    setActivityData(prev => ({
      ...prev,
      [type]: {
        count: (prev[type]?.count || 0) + 1,
        resource: prev[type]?.resource || null,
        isLink: type === 'youtube'
      }
    }));
    setSelectedType(type);
  };

  const handleResourceUpdate = (type, value) => {
    if (!type) return;
    setActivityData(prev => ({
      ...prev,
      [type]: { ...prev[type], resource: value }
    }));
  };

  const removeResource = (type) => {
    setActivityData(prev => ({
      ...prev,
      [type]: { ...prev[type], resource: null }
    }));
  };

  const removeActivity = (type) => {
    setActivityData(prev => {
      const newState = { ...prev };
      delete newState[type];
      return newState;
    });
    if (selectedType === type) setSelectedType("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const generateTask = async () => {
      // 3 second delay for simulation
      await new Promise(resolve => setTimeout(resolve, 3000));
    };

    handleAsyncAction(
      generateTask,
      setIsLoading,
      () => {
        alert("✨ Course generation started!");
        navigate('/');
      }
    );
  };

  const hasResources = Object.values(activityData).some(data => data.resource !== null);

  return (
    <main className="ff2 main-page">
      <HeaderBar />
      <div className="inner-page-content">
        <SideBar />
        
        <div className="course-modal-overlay">
          <div className="course-modal-container">
            <button onClick={() => navigate(-1)} className="modal-back-btn">
              <div className="icon-holder"><ArrowLeft size={18} /></div>
              <span>Go Back</span>
            </button>

            <div className="main-glass-card">
              <header className="modal-header">
                <h3 className="title">Create New Course</h3>
                <p className="subtitle">Design and Preview your curriculum</p>
              </header>

              <form className="course-form" onSubmit={handleSubmit}>
                <div className="form-group">
                  <label className="field-label"><Layout size={16} /> Course Name</label>
                  <input required type="text" placeholder="e.g. Advanced Astrophysics" className="form-input" />
                </div>

                <div className="form-row-grid">
                  <div className="form-group">
                    <label className="field-label"><Users size={16} /> Class</label>
                    <input required type="text" placeholder="Class" className="form-input" />
                  </div>
                  <div className="form-group">
                    <label className="field-label">Division</label>
                    <input required type="text" placeholder="Div" className="form-input" />
                  </div>
                </div>

                <div className="activity-block">
                  <label className="field-label"><Send size={16} /> Course Activities</label>

                  <div className="activities-badge-container">
                    {Object.entries(activityData).length === 0 && (
                      <span className="empty-state-text">Select from dropdown to add activities...</span>
                    )}
                    {Object.entries(activityData).map(([type, data]) => (
                      <div
                        key={type}
                        onClick={() => setSelectedType(type)}
                        className={`activity-badge-item ${selectedType === type ? 'is-selected' : ''}`}
                      >
                        {typeIcons[type]}
                        <span className="type-text">{type}</span>
                        <span className="count-pill">{data.count}</span>
                        {data.resource && <Paperclip size={12} className="attachment-pin" />}
                        <button type="button" onClick={(e) => { e.stopPropagation(); removeActivity(type) }} className="badge-close-btn">
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>

                  <div className="activity-controls-row">
                    <div className="custom-select-box">
                      <select value="" onChange={(e) => handleAddActivity(e.target.value)} className="form-input">
                        <option value="" disabled>+ Add Activity Type</option>
                        <option value="page">Page</option>
                        <option value="pdf">PDF</option>
                        <option value="youtube">YouTube</option>
                        <option value="video">Video</option>
                      </select>
                    </div>

                    <div className="resource-input-wrapper">
                      {selectedType === 'youtube' ? (
                        <div className="youtube-url-field">
                          <input
                            type="url"
                            placeholder="Paste YouTube Link..."
                            value={activityData.youtube?.resource || ""}
                            onChange={(e) => handleResourceUpdate('youtube', e.target.value)}
                            className="form-input"
                          />
                          <LinkIcon size={14} className="input-link-icon" />
                        </div>
                      ) : (
                        <label className={`upload-trigger-btn ${!selectedType ? 'is-disabled' : ''}`}>
                          <Upload size={16} />
                          <span className="upload-text">
                            {selectedType ? `UPLOAD FOR ${selectedType.toUpperCase()}` : "PICK BADGE TO UPLOAD"}
                          </span>
                          <input
                            disabled={!selectedType}
                            type="file"
                            onChange={(e) => handleResourceUpdate(selectedType, e.target.files[0])}
                            hidden
                          />
                        </label>
                      )}
                    </div>
                  </div>
                </div>

                {hasResources && (
                  <div className="preview-resources-section">
                    <h3 className="preview-title">
                      <FileCheck size={14} /> Attached Resources Preview
                    </h3>
                    <div className="preview-cards-grid">
                      {Object.entries(activityData).map(([type, data]) => data.resource && (
                        <div key={`preview-${type}`} className="resource-card-item">
                          <div className="card-left-content">
                            <div className="type-icon-square">{typeIcons[type]}</div>
                            <div className="resource-info">
                              <span className="resource-type-tag">{type} Asset</span>
                              <span className="resource-filename">{type === 'youtube' ? data.resource : data.resource.name}</span>
                            </div>
                          </div>
                          <div className="card-right-actions">
                            {type === 'youtube' ? (
                              <button type="button" onClick={() => setSelectedType('youtube')} className="action-btn edit">
                                <Edit3 size={16} />
                              </button>
                            ) : (
                              <label className="action-btn edit">
                                <Edit3 size={16} />
                                <input type="file" onChange={(e) => handleResourceUpdate(type, e.target.files[0])} hidden />
                              </label>
                            )}
                            <button type="button" onClick={() => removeResource(type)} className="action-btn trash">
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="form-group">
                  <label className="field-label"><Sparkles size={16} className="text-amber" /> AI Magic Prompt ✨</label>
                  <textarea required rows="4" placeholder="Describe your course goal..." className="form-textarea" />
                </div>

                <div className="form-footer">
                  <button type="submit" disabled={isLoading} className={`generate-course-btn ${isLoading ? 'is-loading' : ''}`}>
                    {isLoading ? (
                      <><Loader2 size={20} className="animate-spin" /> Generating...</>
                    ) : (
                      <><Sparkles size={20} /> Generate Course</>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default CourseManagement;